import * as React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from "@mui/material";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import InsightsIcon from "@mui/icons-material/Insights";
import { centered_flex_box } from "../../app/components/Styles";
import { connectWebSocket } from "../../app/store/actions/websocketActions";
import { getLiveEvent } from "../../app/store/actions/eventActions";
import moment from "moment";
import { motion } from "framer-motion";
import { PlatformPage, PlatformPageHeader, PlatformCard } from "../../app/components/platform";
import {
  platformColors,
  platformFonts,
} from "../../app/components/tickitPlatformTheme";

export const LiveEvent = ({
  connectWebSocket,
  getLiveEvent,
  event,
  gates,
  isLoading,
}) => {
  const eventId = useParams().id;
  const [total, setTotal] = React.useState(0);

  function subtotal(gates) {
    return (
      gates
      ?.map(({ ticketsProcessed }) => ticketsProcessed)
      .reduce((sum, i) => sum + i, 0) || 0
    );
  }

  React.useEffect(() => {
    connectWebSocket();
    getLiveEvent({ eventId });
  }, [connectWebSocket, getLiveEvent, eventId]);

  React.useEffect(() => {
    setTotal(subtotal(gates));
  }, [gates]);

  const chipSx = {
    borderRadius: "12px",
    borderColor: platformColors.border2,
    color: platformColors.brand,
    fontFamily: platformFonts.body,
    fontWeight: 600,
    backgroundColor: platformColors.brandPale,
    "& .MuiChip-icon": { color: platformColors.brand },
  };

  if (isLoading) {
    return (
      <PlatformPage maxWidth="md">
        <Box sx={{ ...centered_flex_box, minHeight: "50vh" }}>
          <CircularProgress sx={{ color: platformColors.brand }} />
        </Box>
      </PlatformPage>
    );
  }

  return (
    <PlatformPage maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <PlatformPageHeader
            title={event?.title || "Live event"}
            subtitle="Gate check-ins update in real time while the event is open."
          />

          <PlatformCard title="Live statistics">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-start"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mb: 3 }}
            >
              <Chip
                icon={<InsightsIcon />}
                label={`Gates opened ${moment(event?.gatesOpenAt).fromNow()}`}
                variant="outlined"
                sx={chipSx}
              />
              <Chip
                icon={<InsightsIcon />}
                label={`${moment().isBefore(event?.date) ? "Event starts" : "Event started"} ${moment(event?.date).fromNow()}`}
                variant="outlined"
                sx={chipSx}
              />
            </Stack>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: { xs: 560, sm: 650 },
                  "& .MuiTableCell-root": {
                    py: { xs: 1.25, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: "0.8rem", sm: "0.95rem" },
                    fontFamily: platformFonts.body,
                    color: 'var(--platform-text)',
                    borderColor: platformColors.border,
                  },
                }}
                aria-label="live event table"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: platformColors.surface2 }}>
                    <TableCell sx={{ fontFamily: platformFonts.display, fontWeight: 800, color: 'var(--platform-text)' }}>
                      Username
                    </TableCell>
                    <TableCell sx={{ fontFamily: platformFonts.display, fontWeight: 800, color: 'var(--platform-text)' }} align="right">
                      Assigned gate
                    </TableCell>
                    <TableCell sx={{ fontFamily: platformFonts.display, fontWeight: 800, color: 'var(--platform-text)' }} align="right">
                      Tickets processed
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gates?.map((row) => (
                    <TableRow
                      key={row._id}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": {
                          backgroundColor: platformColors.surface2,
                        },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {row.username}
                      </TableCell>
                      <TableCell align="right">{row.gate}</TableCell>
                      <TableCell align="right">{row.ticketsProcessed}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        fontWeight: 800,
                        fontFamily: platformFonts.display,
                        bgcolor: platformColors.brandPale,
                        color: platformColors.brand,
                      },
                    }}
                  >
                    <TableCell align="right" colSpan={2}>
                      Total
                    </TableCell>
                    <TableCell align="right">{total}</TableCell>
                  </TableRow>
                  {(!gates || gates.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'var(--platform-text-3)' }}>
                        No gate activity yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </PlatformCard>
        </motion.div>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.events?.isLoading,
  event: state?.events?.results?.event,
  gates: state?.events?.results?.gates,
});

const mapDispatchToProps = { connectWebSocket, getLiveEvent };

export default connect(mapStateToProps, mapDispatchToProps)(LiveEvent);
